import React, { useEffect, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Table, Checkbox, columns as coreColumns } from '@ipc/ipc-components';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import 'react-table-hoc-fixed-columns/lib/styles.css';

import { normalizeDateMonth } from '../../utils/date';
import ButtonsList from '../ButtonsList';
import { AggregatedInfo } from './styles';
import { EXCLUDE_DEFAULT_FILTER } from './constants';

const TableFixedColumns = withFixedColumns(Table);

const TableComponent = ({
  columns, actions, loading, data, onSelect = () => null, className, defaultPageSize = 20, noSelect, noActions = false,
  onRowClick = () => null, getTrPropsHandler, styledColumns = [], displayElementCount = false,
  elementCountLabel = '', shouldClearSelected = false,
}) => {
  const {
    TextColumn, DateColumn, NumberColumn, BooleanColumn,
  } = coreColumns(data);
  const [openedRow, updateOpenedRow] = useState();
  const [selected, updateSelected] = useState([]);
  let filteredData = [...data];

  useEffect(() => {
    updateSelected([]);
  }, [shouldClearSelected]);


  const checkboxHandler = id => ({ target: { checked = false } }) => {
    let newSelected = [];
    if (!checked && selected.includes(id)) {
      newSelected = selected.filter(item => item !== id);
    } else if (checked && !selected.includes(id)) {
      newSelected = selected.concat(id);
    }

    updateSelected(newSelected);
    onSelect(newSelected);
  };

  const allCheckboxHandler = ({ target: { checked = false } }) => {
    let newSelected = [];

    if (checked
      || (!!selected.length && selected.length !== filteredData.length)) {
      newSelected = filteredData.map(({ _original }) => _original.id);
    }

    updateSelected(newSelected);
    onSelect(newSelected);
  };

  const tableColumns = columns.slice(0, -1).map((item) => {
    let ColumnType;
    switch (item.type) {
    case 'date':
      ColumnType = DateColumn;
      break;
    case 'number':
      ColumnType = NumberColumn;
      break;
    case 'boolean':
      ColumnType = BooleanColumn;
      break;
    default:
      ColumnType = TextColumn;
      break;
    }

    const output = {
      ...new ColumnType(item.Header, item.accessor),
      className: item.accessor,
      width: item.width,
      Cell: item.Cell,
      filterable: EXCLUDE_DEFAULT_FILTER.includes(item.Header) ? item.filterable : !item.filterable,
    };

    if (item.type === 'date') {
      output.id = item.accessor;
      output.accessor = row => normalizeDateMonth(row[item.accessor]);
    }

    return output;
  });

  if (!noActions) {
    tableColumns.push({
      Header: 'Actions',
      className: 'actions',
      accessor: 'actions',
      resizable: false,
      fixed: 'right',
      width: openedRow === undefined ? 130 : columns[columns.length - 1],
      // eslint-disable-next-line react/prop-types
      Cell: ({ index, original }) => (
        <ButtonsList
          role="button"
          dataTestid="row-actions"
          onToggle={val => updateOpenedRow(val ? index : undefined)}
          open={index === openedRow}
        >
          {actions(original)}
        </ButtonsList>
      ),
    });
  }

  if (!noSelect) {
    tableColumns.unshift({
      Header: 'Select',
      className: 'select',
      accessor: 'select',
      filterable: true,
      filterMethod: () => true,
      // eslint-disable-next-line react/prop-types
      Filter: () => (
        <Checkbox
          indeterminate={!!selected.length && selected.length !== filteredData.length}
          checked={!!selected.length}
          onChange={allCheckboxHandler}
          data-testid="select"
        />
      ),

      // eslint-disable-next-line react/prop-types
      Cell: ({ original: { id } }) => (
        <Checkbox
          checked={selected.includes(id)}
          onChange={checkboxHandler(id)}
          data-testid="select"
        />
      ),
      width: 70,
    });
  }

  const getTdPropsHandler = (state, rowInfo, column, instance) => (
    {
      onClick: (e, handleOriginal) => {
        if (!['Select', 'Actions', 'Origin IMPC(s)'].includes(column.Header)) {
          onRowClick(state, rowInfo, column, instance);
        }

        if (handleOriginal) {
          handleOriginal();
        }
      },
      style: {
        cursor: styledColumns.includes(column.Header) ? 'pointer' : 'auto',
      },
      className: styledColumns.includes(column.Header) ? 'HighlightedCell' : '',
    });

  const getTbodyPropsHandler = (state) => {
    filteredData = [...state.sortedData];
    const { filtered } = state;

    const columnsWithoutActions = state.columns.filter(column => !['Select', 'Actions'].includes(column.Header));
    let filteredColumns;
    if (filtered.length > 0) {
      const filteredWithoutClearFilter = filtered.filter(column => column.value.clearFilter !== true);
      filteredColumns = columnsWithoutActions.filter(column => filteredWithoutClearFilter.map(row => row.id).includes(column.accessor));
    } else {
      filteredColumns = [];
    }
    for (let i = 0; i < columnsWithoutActions.length; i += 1) {
      const column = columnsWithoutActions[i];
      column.headerClassName = 'inactive-filter';
    }
    for (let i = 0; i < filteredColumns.length; i += 1) {
      const column = filteredColumns[i];
      column.headerClassName = 'active-filter';
    }
    return {};
  };

  return (
    <>
      <TableFixedColumns
        uniqClassName={className}
        className={className}
        columns={tableColumns}
        data={filteredData}
        loading={loading}
        minRows={0}
        defaultPageSize={defaultPageSize}
        getTdProps={getTdPropsHandler}
        getProps={getTbodyPropsHandler}
        getTrProps={getTrPropsHandler}
      >
        {(state, makeTable) => {
          let recordsInfoText;
          const {
            filtered, pageRows, pageSize, sortedData, page,
          } = state;

          if (sortedData && sortedData.length > 0) {
            const isFiltered = filtered.length > 0 && filtered.some(row => row.value.clearFilter !== true);
            const totalRecords = sortedData.length;
            const recordsCountFrom = page * pageSize + 1;
            const recordsCountTo = recordsCountFrom + pageRows.length - 1;

            if (isFiltered) recordsInfoText = `${recordsCountFrom}-${recordsCountTo} of ${totalRecords} filtered ${elementCountLabel}`;
            else recordsInfoText = `${recordsCountFrom}-${recordsCountTo} of ${totalRecords} ${elementCountLabel}`;
          } else recordsInfoText = `No ${elementCountLabel}`;

          return (
            <>
              {makeTable()}
              {displayElementCount && (
                <AggregatedInfo>
                  <h4>{recordsInfoText}</h4>
                </AggregatedInfo>
              )}
            </>
          );
        }}
      </TableFixedColumns>
    </>
  );
};

TableComponent.propTypes = {
  loading: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.shape),
  data: PropTypes.arrayOf(PropTypes.shape),
  styledColumns: PropTypes.arrayOf(PropTypes.string),
  actions: PropTypes.func,
  onSelect: PropTypes.func,
  onRowClick: PropTypes.func,
  getTrPropsHandler: PropTypes.func,
  className: PropTypes.string,
  defaultPageSize: PropTypes.number,
  noSelect: PropTypes.bool,
  noActions: PropTypes.bool,
  displayElementCount: PropTypes.bool,
  elementCountLabel: PropTypes.string,
  shouldClearSelected: PropTypes.bool,
};

export default TableComponent;
